/**
 * Chat Message Store
 *
 * 按 sessionId 存储聊天消息状态，解决 SPA 切换页面导致消息丢失的问题。
 * 组件卸载时状态保留，重新挂载时从 store 恢复。
 *
 * 特性：
 * - localStorage 持久化（刷新页面后消息不丢失）
 * - LRU 清理（最多保留 MAX_SESSIONS 个会话，防止内存膨胀）
 * - 错误状态持久化（SSE 失败信息跨页面保留）
 * - 附件安全过滤（File/Blob 不可序列化，入 store 前需转为纯数据）
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ChatMessage } from "../features/chat/api";

const MAX_SESSIONS = 50;
const STORAGE_KEY = "aeterna-chat-message-store";
const STORAGE_VERSION = 1;

interface ChatMessageState {
  messagesBySession: Record<string, ChatMessage[]>;
  isTypingBySession: Record<string, boolean>;
  quickPromptsBySession: Record<string, string[] | null>;
  errorBySession: Record<string, string | null>;
  lastActiveAt: Record<string, number>;
}

interface ChatMessageActions {
  getMessages: (sessionId: string | null) => ChatMessage[];
  setMessages: (sessionId: string, messages: ChatMessage[]) => void;
  appendMessage: (sessionId: string, message: ChatMessage) => void;
  updateMessage: (
    sessionId: string,
    messageId: string,
    updater: (msg: ChatMessage) => ChatMessage,
  ) => void;
  getIsTyping: (sessionId: string | null) => boolean;
  setIsTyping: (sessionId: string, isTyping: boolean) => void;
  getQuickPrompts: (sessionId: string | null) => string[] | null;
  setQuickPrompts: (sessionId: string, prompts: string[] | null) => void;
  getError: (sessionId: string | null) => string | null;
  setError: (sessionId: string, error: string | null) => void;
  clearSession: (sessionId: string) => void;
}

type ChatMessageStore = ChatMessageState & ChatMessageActions;

export const DEFAULT_WELCOME_TEXT =
  "你好，我是 AETERNA 健康顾问。上传体检报告或完成快速评估后，我可以结合你的健康档案给出更贴近个人情况的建议。现在也可以先问我一个健康问题。";

export function getWelcomeMessage(): ChatMessage {
  return {
    id: "welcome",
    role: "ai",
    text: DEFAULT_WELCOME_TEXT,
    time: "刚刚",
  };
}

/** 获取所有 session 的并集 key */
function allSessionKeys(state: ChatMessageState): string[] {
  const keys = new Set<string>();
  Object.keys(state.messagesBySession).forEach((k) => keys.add(k));
  Object.keys(state.isTypingBySession).forEach((k) => keys.add(k));
  Object.keys(state.quickPromptsBySession).forEach((k) => keys.add(k));
  Object.keys(state.errorBySession).forEach((k) => keys.add(k));
  Object.keys(state.lastActiveAt).forEach((k) => keys.add(k));
  return Array.from(keys);
}

/** 按 lastActiveAt 清理超出的旧会话 */
function enforceLRU(state: ChatMessageState): ChatMessageState {
  // 快速路径：消息数远低于上限时跳过昂贵的 key 集合构建
  const trackedCount =
    Object.keys(state.messagesBySession).length +
    Object.keys(state.isTypingBySession).length +
    Object.keys(state.quickPromptsBySession).length +
    Object.keys(state.errorBySession).length +
    Object.keys(state.lastActiveAt).length;
  if (trackedCount <= MAX_SESSIONS - 5) return state;

  const sessionIds = allSessionKeys(state);
  if (sessionIds.length <= MAX_SESSIONS) return state;

  const sorted = sessionIds
    .map((id) => ({ id, at: state.lastActiveAt[id] ?? 0 }))
    .sort((a, b) => a.at - b.at);

  const toRemove = sorted
    .slice(0, sessionIds.length - MAX_SESSIONS)
    .map((s) => s.id);
  if (toRemove.length === 0) return state;

  const msgs = { ...state.messagesBySession };
  const typing = { ...state.isTypingBySession };
  const prompts = { ...state.quickPromptsBySession };
  const errors = { ...state.errorBySession };
  const active = { ...state.lastActiveAt };

  for (const id of toRemove) {
    delete msgs[id];
    delete typing[id];
    delete prompts[id];
    delete errors[id];
    delete active[id];
  }

  return {
    ...state,
    messagesBySession: msgs,
    isTypingBySession: typing,
    quickPromptsBySession: prompts,
    errorBySession: errors,
    lastActiveAt: active,
  };
}

function touch(state: ChatMessageState, sessionId: string): ChatMessageState {
  return {
    ...state,
    lastActiveAt: { ...state.lastActiveAt, [sessionId]: Date.now() },
  };
}

/** 过滤掉不可序列化的字段（File/Blob），确保 persist 安全 */
function sanitizeMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((msg) => {
    if (!msg.attachments) return msg;
    return {
      ...msg,
      attachments: msg.attachments.map((a) => ({
        attachmentId: a.attachmentId,
        url: a.url,
        mimeType: a.mimeType,
        fileName: a.fileName,
        sizeBytes: a.sizeBytes,
        mediaKind: a.mediaKind,
        uploadStatus: a.uploadStatus,
        dedupeKey: a.dedupeKey,
        groupId: a.groupId,
        fileOrder: a.fileOrder,
      })),
    };
  });
}

export const useChatMessageStore = create<ChatMessageStore>()(
  persist(
    (set, get) => ({
      messagesBySession: {},
      isTypingBySession: {},
      quickPromptsBySession: {},
      errorBySession: {},
      lastActiveAt: {},

      getMessages: (sessionId) => {
        if (!sessionId) return [getWelcomeMessage()];
        return get().messagesBySession[sessionId] ?? [getWelcomeMessage()];
      },

      setMessages: (sessionId, messages) => {
        set((state) =>
          enforceLRU(
            touch(
              {
                ...state,
                messagesBySession: {
                  ...state.messagesBySession,
                  [sessionId]: messages,
                },
              },
              sessionId,
            ),
          ),
        );
      },

      appendMessage: (sessionId, message) => {
        set((state) => {
          const existing = state.messagesBySession[sessionId] ?? [];
          if (existing.some((m) => m.id === message.id)) return state;
          return enforceLRU(
            touch(
              {
                ...state,
                messagesBySession: {
                  ...state.messagesBySession,
                  [sessionId]: [...existing, message],
                },
              },
              sessionId,
            ),
          );
        });
      },

      updateMessage: (sessionId, messageId, updater) => {
        set((state) => {
          const existing = state.messagesBySession[sessionId];
          if (!existing) return state;
          return enforceLRU(
            touch(
              {
                ...state,
                messagesBySession: {
                  ...state.messagesBySession,
                  [sessionId]: existing.map((m) =>
                    m.id === messageId ? updater(m) : m,
                  ),
                },
              },
              sessionId,
            ),
          );
        });
      },

      getIsTyping: (sessionId) => {
        if (!sessionId) return false;
        return get().isTypingBySession[sessionId] ?? false;
      },

      setIsTyping: (sessionId, isTyping) => {
        set((state) =>
          enforceLRU(
            touch(
              {
                ...state,
                isTypingBySession: {
                  ...state.isTypingBySession,
                  [sessionId]: isTyping,
                },
              },
              sessionId,
            ),
          ),
        );
      },

      getQuickPrompts: (sessionId) => {
        if (!sessionId) return null;
        return get().quickPromptsBySession[sessionId] ?? null;
      },

      setQuickPrompts: (sessionId, prompts) => {
        set((state) =>
          enforceLRU(
            touch(
              {
                ...state,
                quickPromptsBySession: {
                  ...state.quickPromptsBySession,
                  [sessionId]: prompts,
                },
              },
              sessionId,
            ),
          ),
        );
      },

      getError: (sessionId) => {
        if (!sessionId) return null;
        return get().errorBySession[sessionId] ?? null;
      },

      setError: (sessionId, error) => {
        set((state) =>
          enforceLRU(
            touch(
              {
                ...state,
                errorBySession: { ...state.errorBySession, [sessionId]: error },
              },
              sessionId,
            ),
          ),
        );
      },

      clearSession: (sessionId) => {
        set((state) => {
          const { [sessionId]: _msgs, ...restMsgs } = state.messagesBySession;
          const { [sessionId]: _typing, ...restTyping } =
            state.isTypingBySession;
          const { [sessionId]: _prompts, ...restPrompts } =
            state.quickPromptsBySession;
          const { [sessionId]: _error, ...restErrors } = state.errorBySession;
          const { [sessionId]: _at, ...restAt } = state.lastActiveAt;
          return {
            ...state,
            messagesBySession: restMsgs,
            isTypingBySession: restTyping,
            quickPromptsBySession: restPrompts,
            errorBySession: restErrors,
            lastActiveAt: restAt,
          };
        });
      },
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      migrate: (persistedState: any, version) => {
        if (version < STORAGE_VERSION) {
          // v0 → v1: 补齐可能缺失的字段
          persistedState.errorBySession ??= {};
          persistedState.lastActiveAt ??= {};
        }
        // 过滤不可序列化的附件数据
        if (persistedState.messagesBySession) {
          for (const sid of Object.keys(persistedState.messagesBySession)) {
            persistedState.messagesBySession[sid] = sanitizeMessages(
              persistedState.messagesBySession[sid],
            );
          }
        }
        return persistedState as ChatMessageState;
      },
      partialize: (state) => ({
        messagesBySession: Object.fromEntries(
          Object.entries(state.messagesBySession).map(([k, v]) => [
            k,
            sanitizeMessages(v),
          ]),
        ),
        isTypingBySession: state.isTypingBySession,
        quickPromptsBySession: state.quickPromptsBySession,
        errorBySession: state.errorBySession,
        lastActiveAt: state.lastActiveAt,
      }),
    },
  ),
);
